import { useCallback, useId } from "react";
import styled from "@emotion/styled";
import { Button, buttonClasses, Typography } from "@kaltura/ds-react-components";
import { encodeUnicode, formatEntryThumbnailUrl, stripTags, translate } from "@kaltura/mediaspace-shared-utils";
import TruncateMarkup from "react-truncate-markup";
import { systemWidthSize } from "@kaltura/mediaspace-shared-styled";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { contrastTextShadow } from "@kaltura/mediaspace-shared-ui";
import { ButtonClickAnalyticsType, EventPlatformEntry } from "@kaltura/mediaspace-shared-types";

interface Dimensions {
    height: number;
    width: number;
}

interface Props {
    item: EventPlatformEntry;
    modalBoxMaskColor?: string;
    tabIndex?: number;
    onClick: (entry: EventPlatformEntry) => void;
}

const OpacityLayer = styled("div")(({ theme }) => ({
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    pointerEvents: "none",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    borderRadius: theme.kaltura.shape.roundness2,
}));

const TextBox = styled("div")({
    zIndex: 1,
    width: "100%",
    textShadow: contrastTextShadow,
});

const TruncatedName = styled(Typography)(({ theme }) => ({
    color: theme.palette.common.white,
    width: "100%",
    whiteSpace: "normal",
}));

const TruncatedDescription = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(2),
    color: theme.palette.common.white,
    width: "100%",
    whiteSpace: "normal",
}));

const StyledReadMore = styled("div")(({ theme }) => ({
    marginTop: theme.spacing(2),
    color: theme.palette.common.white,
    fontWeight: theme.kaltura.typography.fontWeightBold,
    fontSize: theme.typography.pxToRem(16),
    lineHeight: 1.31,
}));

const PaddedButtonContainer = styled("div")(({ theme }) => ({
    padding: theme.typography.pxToRem(4), // Add small padding to the button container to prevent the focus-visible styles from being clipped
    justifyContent: "center",
    display: "flex",
}));

const StyledButton = styled(
    Button,
    {
        shouldForwardProp: (prop) =>
            !["image", "extraLargeSize", "largeSize", "mediumSize", "smallSize"].includes(prop),
    }
)<{
    image: string;
    extraLargeSize: Dimensions;
    largeSize: Dimensions;
    mediumSize: Dimensions;
    smallSize: Dimensions;
}>(({ theme, image, extraLargeSize, largeSize, mediumSize, smallSize }) => ({
    whiteSpace: "nowrap",
    display: "flex",
    backgroundImage: `url(${image})`,
    alignItems: "flex-start",
    justifyContent: "flex-end",
    padding: 24,
    border: "none",
    flexDirection: "column",
    position: "relative",
    width: extraLargeSize.width,
    height: extraLargeSize.height,
    backgroundPosition: "center",
    backgroundSize: "cover",
    borderRadius: theme.kaltura.shape.roundness2,
    cursor: "pointer",
    boxSizing: "border-box",
    textAlign: "left",
    [`& .${buttonClasses.label}`]: {
        justifyContent: "start",
    },
    [theme.breakpoints.down(theme.breakpoints.values.xl - 0.05)]: {
        width: largeSize.width,
        height: largeSize.height,
    },
    [theme.breakpoints.down(theme.breakpoints.values.lg - 0.05)]: {
        width: mediumSize.width,
        height: mediumSize.height,
    },
    [theme.breakpoints.down(theme.breakpoints.values.sm - 0.05)]: {
        width: smallSize.width,
        height: smallSize.height,
    },
}));

/**
 * NewsItem Component displays news entry
 */
const NewsItem = (props: Props) => {
    const { item: entry, tabIndex = 0, onClick } = props;

    const itemId = useId();

    // calculate thumbnail dimensions
    const thumbnailDimensions = (size: keyof typeof systemWidthSize, spacing: number) => {
        const totalWidth = systemWidthSize[size];
        const width = Math.floor((totalWidth - 8 * spacing * 2) / 3);
        const height = Math.round((width * 10) / 8);
        return { width: width, height: height };
    };

    const extraLargeSize = thumbnailDimensions("xl", 4);
    const largeSize = thumbnailDimensions("lg", 4);
    const mediumSize = thumbnailDimensions("md", 2);
    const smallSize = {
        width: mediumSize.width - 26,
        height: ((mediumSize.width - 26) * 10) / 8,
    };

    const thumbnailUrl = formatEntryThumbnailUrl(entry.thumbnailUrl, extraLargeSize.width, extraLargeSize.height);
    const plainDescription = stripTags(entry.description ?? "");

    const sendButtonAnalytics = useButtonAnalytics();

    const handleClick = useCallback(() => {
        onClick(entry);
        sendButtonAnalytics("News Item - Open Item", ButtonClickAnalyticsType.LAUNCH);
    }, [entry, onClick, sendButtonAnalytics]);

    return (
        <PaddedButtonContainer>
            <StyledButton
                image={thumbnailUrl}
                aria-label={translate("Read more about %1", [entry.name])}
                aria-describedby={itemId}
                onClick={handleClick}
                data-analytics={encodeUnicode(entry.name)}
                extraLargeSize={extraLargeSize}
                largeSize={largeSize}
                mediumSize={mediumSize}
                smallSize={smallSize}
                tabIndex={tabIndex}
            >
                <OpacityLayer className="kms-ds-news-item-opacity-layer" />
                <TextBox className="kms-ds-news-item-content">
                    <TruncateMarkup lines={3}>
                        <TruncatedName
                            variant={"h3"}
                            component={"h2"}
                            className="kms-ds-news-item-title chromatic-ignore"
                        >
                            {entry.name}
                        </TruncatedName>
                    </TruncateMarkup>
                    <TruncateMarkup lines={5}>
                        <TruncatedDescription
                            id={itemId}
                            variant={"body1"}
                            className="kms-ds-news-item-description chromatic-ignore"
                        >
                            {plainDescription}
                        </TruncatedDescription>
                    </TruncateMarkup>
                    <StyledReadMore className="kms-ds-news-item-read-more">{translate("Read More")}</StyledReadMore>
                </TextBox>
            </StyledButton>
        </PaddedButtonContainer>
    );
};

export default NewsItem;
